/** Matches ActivityKind on the server: every kind's filter key is `<kind>_id`. */
export const activityReportFilter = (kind: string, entityId: string) =>
  ({ [`${kind}_id`]: entityId });

const pad = (n: number) => String(n).padStart(2, '0');
const stamp = (d: Date) =>
  `${d.getUTCFullYear()}-${pad(d.getUTCMonth() + 1)}-${pad(d.getUTCDate())} ${pad(d.getUTCHours())}:00:00`;

export const ACTIVITY_REPORT_HOURS = 48;

/** The report returns only hours that saw rows, and on a categorical axis gaps close up —
 *  an outage would render as two adjacent bars. */
export function fillHourlyGaps<T extends { group_key: unknown }>(
  rows: T[], hours = ACTIVITY_REPORT_HOURS,
): Array<T | { group_key: string }> {
  // ClickHouse returns ISO ("2026-09-17T18:00:00Z"); a hand-built "2026-09-17 18:00:00"
  // matches nothing and silently zero-fills every graph. Normalise both sides.
  const hourKey = (v: unknown) => {
    const t = Date.parse(String(v).replace(' ', 'T').replace(/Z?$/, 'Z'));
    return Number.isNaN(t) ? String(v) : new Date(t).toISOString().slice(0, 13);
  };

  const byKey = new Map(rows.map((r) => [hourKey(r.group_key), r]));
  const top = new Date();
  top.setUTCMinutes(0, 0, 0);

  const out: Array<T | { group_key: string }> = [];
  for (let i = hours - 1; i >= 0; i--) {
    const at = new Date(top.getTime() - i * 3_600_000);
    const hit = byKey.get(hourKey(at.toISOString()));
    out.push(hit ?? { group_key: at.toISOString().slice(0, 19).replace('T', ' ') });
  }
  return out;
}

/** Matches the chip beside it: ACTIVITY_TZ is UTC and it counts non-bot visits. The report's
 *  own -7 default would put the graph's day seven hours off the figures above it. */
export function activityReportWindow(hours = ACTIVITY_REPORT_HOURS) {
  const now = new Date();
  const from = new Date(now.getTime() - hours * 3_600_000);
  return {
    date_from: stamp(from),
    date_to: stamp(now).replace(':00:00', ':59:59'),
    timezone: 0,
    include_bots: false,
    conv_time_basis: 'conversion' as const,
  };
}
